import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowLeft, FaSolarPanel, FaWind, FaIndustry, FaChartLine, FaLeaf, FaGraduationCap, 
         FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaBookOpen, FaTools, FaLaptopCode, 
         FaCertificate, FaAward, } from 'react-icons/fa';
import "./stem.css";

const EnergyPage = () => {
  // Carousel state and functions
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const carouselImages = [
    {
      url: "/api/placeholder/800/400",
      alt: "Solar panel installation",
      caption: "Students learning solar panel installation techniques"
    },
    {
      url: "/api/placeholder/800/400",
      alt: "Wind farm field trip",
      caption: "Field trip to offshore wind farm facility"
    },
    {
      url: "/api/placeholder/800/400",
      alt: "Energy lab experiments",
      caption: "Hands-on experiments in our energy efficiency lab"
    },
    {
      url: "/api/placeholder/800/400",
      alt: "Industry workshop",
      caption: "Industry experts conducting specialized workshops"
    },
    {
      url: "/api/placeholder/800/400",
      alt: "Graduation day",
      caption: "Students receiving their energy sector certifications"
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === carouselImages.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? carouselImages.length - 1 : prev - 1));
  };

  // Course roadmap data
  const courseRoadmap = [
    {
      phase: "Foundation",
      duration: "2 months",
      icon: <FaBookOpen />,
      description: "Core concepts of energy systems and fundamentals of renewable technologies",
      keyTopics: ["Energy conversion principles", "Basic electrical systems", "Environmental science", "Sustainability fundamentals"]
    },
    {
      phase: "Specialization",
      duration: "3 months",
      icon: <FaTools />,
      description: "Focused learning on specific renewable energy technology tracks",
      keyTopics: ["Solar/Wind/Bioenergy specialization", "System design principles", "Installation techniques", "Maintenance protocols"]
    },
    {
      phase: "Applied Projects",
      duration: "2 months",
      icon: <FaLaptopCode />,
      description: "Hands-on implementation of energy systems and solutions",
      keyTopics: ["System modeling and simulation", "Energy audit practice", "Field installations", "Performance analysis"]
    },
    {
      phase: "Industry Integration",
      duration: "1 month",
      icon: <FaIndustry />,
      description: "Industry exposure through internships and live projects",
      keyTopics: ["Industry placements", "Mentorship programs", "Site visits", "Case study analysis"]
    },
    {
      phase: "Certification",
      duration: "Final 2 weeks",
      icon: <FaCertificate />,
      description: "Final assessment and industry-recognized certification",
      keyTopics: ["Comprehensive evaluation", "Project defense", "Certification exam", "Industry panel review"]
    }
  ];

  // Certification types
  const certificationTypes = [
    {
      title: "Professional Energy Specialist",
      level: "Advanced",
      recognition: "Industry-recognized certification with global acceptance",
      benefits: ["Access to premium job opportunities", "Industry network membership", "Ongoing professional development"]
    },
    {
      title: "Renewable Technology Expert",
      level: "Intermediate to Advanced",
      recognition: "Technical specialization with practical endorsement",
      benefits: ["Technical role qualification", "Specialized career advancement", "Project leadership eligibility"]
    },
    {
      title: "Energy Sector Foundation",
      level: "Entry Level",
      recognition: "Basic qualification for energy sector roles",
      benefits: ["Entry-level position eligibility", "Academic progression pathway", "Fundamental knowledge validation"]
    }
  ];

  const programCategories = [
    {
      title: "Renewable Energy Education",
      description: "Specialized training in sustainable energy technologies",
      courses: [
        { name: "Solar Energy Engineering", description: "PV technology, solar thermal systems, design and implementation", icon: <FaSolarPanel /> },
        { name: "Wind Energy Systems", description: "Wind turbine technology, offshore and onshore installations", icon: <FaWind /> },
        { name: "Bio-Energy & Alternative Fuels", description: "Biomass conversion, biofuels, and waste-to-energy technologies", icon: <FaLeaf /> },
        { name: "Energy Storage Solutions", description: "Battery technologies, thermal storage, and grid integration", icon: <FaIndustry /> }
      ]
    },
    {
      title: "Energy Sector Examinations",
      description: "Preparation for competitive exams and admissions",
      courses: [
        { name: "IIT JAM for Energy Studies", description: "Specialized coaching for Joint Admission Test for Masters in energy disciplines", icon: <FaGraduationCap /> },
        { name: "GATE (Energy & Power Sector)", description: "Graduate Aptitude Test preparation for energy engineering", icon: <FaGraduationCap /> },
        { name: "PSU Recruitment (Energy Sector)", description: "Coaching for NTPC, PGCIL, NHPC and other energy PSU exams", icon: <FaIndustry /> },
        { name: "Energy Management Certification", description: "Preparation for certified energy manager and auditor exams", icon: <FaChartLine /> }
      ]
    },
    {
      title: "Energy Industry Skills",
      description: "Job-oriented training for energy sector careers",
      courses: [
        { name: "Smart Grid Technologies", description: "Modern power distribution, grid management, and integration", icon: <FaIndustry /> },
        { name: "Energy Efficiency & Auditing", description: "Industrial and commercial energy audit techniques and conservation", icon: <FaChartLine /> },
        { name: "Renewable Project Management", description: "Planning, execution, and management of renewable energy projects", icon: <FaSolarPanel /> },
        { name: "Energy Policy & Economics", description: "Regulatory frameworks, carbon markets, and economic analysis", icon: <FaChartLine /> }
      ]
    }
  ];

  return (
    <div className="program-page energy-theme my-5 py-5">
      <div className="program-header">
        <Link to="/" className="text-start">
          <FaArrowLeft /> Back to Home
        </Link>
        <div className="program-title">
          <FaSolarPanel className="program-header-icon" />
          <h1>Energy Sector Programs</h1>
        </div>
        <p className="program-subtitle">Renewable energy technologies, sustainability practices, and energy careers</p>
      </div>

      {/* Carousel Section */}
      <div className="carousel-container my-5">
        <h2 className="text-center mb-4">Our Energy Training Facilities</h2>
        <div className="carousel-wrapper">
          <button className="carousel-btn prev-btn" onClick={prevSlide}>
            <FaChevronLeft />
          </button>
          <div className="carousel-slides">
            {carouselImages.map((image, index) => (
              <div 
                key={index} 
                className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
                style={{display: index === currentSlide ? 'block' : 'none'}}
              >
                <img src={image.url} alt={image.alt} className="carousel-image" />
                <div className="carousel-caption">
                  <p>{image.caption}</p>
                </div>
              </div>
            ))}
            <div className="carousel-indicators">
              {carouselImages.map((_, index) => (
                <button 
                  key={index} 
                  className={`carousel-indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                >
                </button>
              ))}
            </div>
          </div>
          <button className="carousel-btn next-btn" onClick={nextSlide}>
            <FaChevronRight />
          </button>
        </div>
      </div>

      <div className="program-overview">
        <h2>Why Choose Our Energy Sector Training?</h2>
        <div className="features-grid">
          <div className="feature-card">
            <h3>Industry Connections</h3>
            <p>Partnerships with leading energy companies and research institutions</p>
          </div>
          <div className="feature-card">
            <h3>Practical Training</h3>
            <p>Hands-on experience with renewable energy equipment and installations</p>
          </div>
          <div className="feature-card">
            <h3>Future-Ready Skills</h3>
            <p>Focus on emerging technologies and industry trends</p>
          </div>
          <div className="feature-card">
            <h3>Sustainability Focus</h3>
            <p>Integrated approach combining technical and environmental aspects</p>
          </div>
        </div>
      </div>

      {/* Course Roadmap Section */}
      <div className="roadmap-section my-5 py-4">
        <h2 className="text-center mb-4">Your Learning Journey: Course Roadmap</h2>
        <p className="text-center mb-5">Our structured approach ensures a comprehensive learning experience from basics to expertise</p>
        
        <div className="roadmap-timeline">
          {courseRoadmap.map((phase, index) => (
            <div className={`roadmap-item ${index % 2 === 0 ? 'left' : 'right'}`} key={index}>
              <div className="roadmap-content">
                <div className="roadmap-icon">{phase.icon}</div>
                <h3>{phase.phase}</h3>
                <span className="roadmap-duration">{phase.duration}</span>
                <p>{phase.description}</p>
                <ul className="roadmap-topics">
                  {phase.keyTopics.map((topic, i) => (
                    <li key={i}><FaMapMarkerAlt className="topic-marker" /> {topic}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="courses-section">
        {programCategories.map((category, index) => (
          <div className="course-category" key={index}>
            <h2>{category.title}</h2>
            <p className="category-description">{category.description}</p>
            
            <div className="courses-grid">
              {category.courses.map((course, courseIndex) => (
                <div className="course-card" key={courseIndex}>
                  <div className="course-icon">{course.icon}</div>
                  <h3>{course.name}</h3>
                  <p>{course.description}</p>
                  <button className="enroll-btn">Learn More</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* New Certification Section */}
      <div className="certification-section my-5 py-4">
        <h2 className="text-center mb-2">Internationally Recognized Certification</h2>
        <p className="text-center mb-5">Our programs offer industry-respected certifications to boost your career prospects</p>
        
        <div className="certification-container">
          <div className="certification-details">
            <div className="certification-header">
              <FaAward className="certification-icon" />
              <h3>Certification Benefits</h3>
            </div>
            
            <ul className="certification-benefits">
              <li>Globally recognized qualification in renewable energy technologies</li>
              <li>Digital verification system with secure blockchain technology</li>
              <li>Endorsed by leading energy companies and industry associations</li>
              <li>Includes lifetime access to alumni network and resources</li>
              <li>Regular updates with continuing education options</li>
            </ul>
            
            <div className="certification-types">
              <h4>Available Certification Levels</h4>
              <div className="cert-types-container">
                {certificationTypes.map((cert, index) => (
                  <div className="cert-type-card" key={index}>
                    <h5>{cert.title}</h5>
                    <span className="cert-level">{cert.level}</span>
                    <p>{cert.recognition}</p>
                    <ul>
                      {cert.benefits.map((benefit, i) => (
                        <li key={i}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="industry-projects">
        <h2>Live Projects & Field Visits</h2>
        <p>Our energy sector programs include visits to operational renewable energy installations and participation in real projects</p>
        <div className="projects-grid">
          <div className="project-card">
            <h3>Solar Park Assessment</h3>
            <p>Energy yield analysis and optimization for commercial solar installations</p>
          </div>
          <div className="project-card">
            <h3>Wind Farm Monitoring</h3>
            <p>Performance monitoring and predictive maintenance for wind turbines</p>
          </div>
          <div className="project-card">
            <h3>Energy-Efficient Building Design</h3>
            <p>Green building technology integration and certification</p>
          </div>
        </div>
      </div>

      <div className="testimonials-section">
        <h2>Success Stories</h2>
        <div className="testimonials-container">
          <div className="testimonial-card">
            <p>"The solar energy engineering program gave me both theoretical knowledge and practical skills. I'm now working as a PV systems designer at a leading renewable energy company."</p>
            <p className="testimonial-author">- Vikram S., Solar Design Engineer</p>
          </div>
          <div className="testimonial-card">
            <p>"The GATE preparation for energy engineering helped me secure admission to IIT Bombay's Renewable Energy program. The focused approach and quality study material made all the difference."</p>
            <p className="testimonial-author">- Meera P., M.Tech Student</p>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <h2>Power Your Career in the Energy Sector</h2>
        <p>Join our specialized renewable energy and sustainability programs to be part of the clean energy revolution</p>
        <button className="primary-btn">Schedule a Free Consultation</button>
      </div>
    </div>
  );
};

export default EnergyPage;